import type { GetActiveScriptResponse } from '@compass/client/types/script';
import { scriptService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/** True when an axios-style error carries a 404 Not Found status. */
const isNotFound = (error: unknown): boolean => {
  const e = error as { APIHttpStatus?: number; response?: { status?: number } };
  return e?.APIHttpStatus === 404 || e?.response?.status === 404;
};

/**
 * Fetches the active Call Script for a workflow via `GetActiveScript`.
 *
 * Mirrors `useCase`: keyed by workflowId (+ selected org/program from the store),
 * enabled only when all three are present, with a 5-minute staleTime.
 *
 * A workflow without an active script (`NotFound` / 404) is NOT an error for
 * consumers: the query resolves with an empty response (`script` undefined) so
 * the Human Call tab still works off the schema/board. Other errors propagate
 * normally.
 */
export const useActiveScript = (workflowId: string | null | undefined) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const isEnabled = useMemo(
    () => Boolean(workflowId && selectedOrganizationId && selectedProgramId),
    [workflowId, selectedOrganizationId, selectedProgramId],
  );

  return useQuery({
    queryKey: queryKeys.scripts.activeByWorkflow(
      selectedOrganizationId || '',
      selectedProgramId || '',
      workflowId || '',
    ),
    queryFn: async (): Promise<GetActiveScriptResponse> => {
      try {
        return await scriptService.getActiveScript({
          organization_id: selectedOrganizationId!,
          program_id: selectedProgramId!,
          workflow_id: workflowId!,
        });
      } catch (error) {
        // A workflow with no active script resolves to "no script" rather than
        // surfacing an error to the UI.
        if (isNotFound(error)) {
          return {};
        }
        throw error;
      }
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - same as useCase
    gcTime: 10 * 60 * 1000, // 10 minutes - same as useCase
  });
};
